<template>
  <CContainer fluid class="download-landing">
    <div class="download-landing__bg-circles">
      <div class="circle circle-1"/>
      <div class="circle circle-2"/>
      <div class="circle circle-3"/>
    </div>

    <CRow class="download-landing__wrapper justify-content-center align-items-center">
      <CCol col="12" sm="9" md="6" lg="4" xl="3">
        <div class="download-landing__brand">
          <CIcon
            class="download-landing__logo"
            name="logo"
            size="custom-size"
            :height="32"
            viewBox="0 0 136 34"
          />
        </div>

        <div class="download-landing__card">
          <PTransparentLoader :loading="status === 'loading'">
            <div v-if="status === 'ready'">
              <header class="download-landing__hero">
                <h1 class="download-landing__title">
                  <translate translate-context="download_landing.title">Your files are ready</translate>
                </h1>
                <p class="download-landing__subtitle">
                  <translate translate-context="download_landing.subtitle">
                    Save them to your device before the link expires.
                  </translate>
                </p>
              </header>

              <div class="download-landing__stats">
                <div class="stat-card">
                  <div class="stat-card__label">
                    <translate translate-context="download_landing.stats">Files</translate>
                  </div>
                  <div class="stat-card__value">{{ totalFileCount }}</div>
                </div>
                <div class="stat-card" v-if="totalSizeBytes > 0">
                  <div class="stat-card__label">
                    <translate translate-context="download_landing.stats">Size</translate>
                  </div>
                  <div class="stat-card__value">{{ totalSizeFormatted }}</div>
                </div>
                <div class="stat-card" v-if="expiresAt">
                  <div class="stat-card__label">
                    <translate translate-context="download_landing.stats">Expires in</translate>
                  </div>
                  <div class="stat-card__value">{{ expiresAtFormatted }}</div>
                </div>
              </div>

              <CAlert v-if="failedCount > 0" color="warning" class="text-left">
                <translate translate-context="download_landing.warning"
                           :translate-n="failedCount"
                           translate-plural="%{ failedCount } files couldn't be included."
                           :translate-params="{failedCount}">
                  %{ failedCount } file couldn't be included.
                </translate>
              </CAlert>

              <CButton
                color="primary"
                size="lg"
                block
                class="download-landing__btn"
                :disabled="isAnyDownloading || isAllHandled"
                @click="onDownloadAll">
                <CIcon name="cil-cloud-download" class="mr-2"/>
                <template v-if="isAllHandled">
                  <translate v-if="hasBrowserManagedDownloads" translate-context="download_landing.button">Downloads started</translate>
                  <translate v-else translate-context="download_landing.button">Download complete</translate>
                </template>
                <template v-else-if="downloadActive">
                  <translate translate-context="download_landing.button"
                             :translate-params="{done: handledPartsCount, total: parts.length}">
                    Downloading %{ done } of %{ total }…
                  </translate>
                </template>
                <template v-else>
                  <translate translate-context="download_landing.button">Start download</translate>
                </template>
              </CButton>

              <div class="download-landing__overall" v-if="(downloadActive || isAllHandled) && parts.length > 1">
                <div class="overall-bar">
                  <div class="overall-bar__fill"
                       :class="{'overall-bar__fill--done': isAllHandled}"
                       :style="{width: `${overallPercentage}%`}"/>
                </div>
                <div class="overall-bar__label">{{ overallPercentage }}%</div>
              </div>

              <section class="download-landing__section">
                <header class="download-landing__section-head">
                  <span class="download-landing__section-title">
                    <translate translate-context="download_landing.section">What's included</translate>
                  </span>
                  <span class="download-landing__section-meta" v-if="parts.length > 1">
                    <translate translate-context="download_landing.section"
                               :translate-n="parts.length"
                               translate-plural="%{ count } ZIP files"
                               :translate-params="{count: parts.length}">
                      %{ count } ZIP file
                    </translate>
                  </span>
                </header>

                <ul class="download-landing__parts">
                  <li v-for="part in parts" :key="part.index"
                      class="part" :class="`part--${part.status}`">
                    <div class="part__icon">
                      <CIcon v-if="part.status === 'failed'" name="cil-warning"/>
                      <CIcon v-else-if="part.status === 'downloading'" name="cil-cloud-download" class="part__icon--pulsing"/>
                      <CIcon v-else-if="part.status === 'done' || part.status === 'started'" name="cil-check-circle"/>
                      <CIcon v-else name="cil-file"/>
                    </div>

                    <div class="part__main">
                      <div class="part__filename">{{ part.filename }}</div>
                      <div class="part__sub">
                        <template v-if="part.sizeBytes > 0">{{ formatBytes(part.sizeBytes) }} · </template>
                        <translate translate-context="download_landing.part"
                                   :translate-n="part.fileCount"
                                   translate-plural="%{ count } files"
                                   :translate-params="{count: part.fileCount}">
                          %{ count } file
                        </translate>
                      </div>
                    </div>

                    <div class="part__status">
                      <CIcon v-if="part.status === 'done'" name="cil-check-circle" class="part__status-icon part__status-icon--done"/>
                      <CIcon v-else-if="part.status === 'started'" name="cil-check-circle" class="part__status-icon part__status-icon--started"/>
                      <CIcon v-else-if="part.status === 'failed'" name="cil-reload" class="part__status-icon part__status-icon--retry"
                             @click.native="onDownloadPart(part.index)"/>
                      <span v-else-if="part.status === 'downloading' && part.downloadMode === 'fetch_progress' && part.percentage > 0"
                            class="part__status-percent">{{ part.percentage }}%</span>
                      <CIcon v-else-if="part.status === 'downloading'" name="cil-cloud-download"
                             class="part__status-icon part__status-icon--pulsing"/>
                      <CIcon v-else name="cil-cloud-download"
                             class="part__status-icon part__status-icon--download"
                             @click.native="onDownloadPart(part.index)"/>
                    </div>
                  </li>
                </ul>
              </section>
            </div>

            <div v-else-if="status === 'not_found'" class="download-landing__error">
              <CIcon name="cil-warning" class="download-landing__error-icon"/>
              <h2 v-translate translate-context="download_landing.error">Archive not found</h2>
              <p class="text-muted" v-translate translate-context="download_landing.error">
                The download link is invalid or has been revoked.
              </p>
            </div>

            <div v-else-if="status === 'expired'" class="download-landing__error">
              <CIcon name="cil-clock" class="download-landing__error-icon"/>
              <h2 v-translate translate-context="download_landing.error">Download link expired</h2>
              <p class="text-muted" v-translate translate-context="download_landing.error">
                This archive is no longer available. Please request a new download.
              </p>
            </div>

            <div v-else-if="status === 'error'" class="download-landing__error">
              <CIcon name="cil-warning" class="download-landing__error-icon"/>
              <h2 v-translate translate-context="download_landing.error">Something went wrong</h2>
              <p class="text-muted">{{ errorMessage || $gettext('We could not load your archive. Please try again later.') }}</p>
            </div>
          </PTransparentLoader>
        </div>

        <TheFooter class="download-landing__footer"/>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import TheFooter from '@/containers/TheFooter.vue'

export default {
  name: 'DownloadLanding',
  components: {PTransparentLoader, TheFooter},

  computed: {
    ...mapGetters('mediaDownloadLanding', [
      'status', 'parts', 'expiresAt', 'totalFileCount', 'totalSizeBytes', 'failedCount',
      'errorMessage', 'downloadActive', 'overallPercentage',
      'handledPartsCount', 'isAllHandled', 'hasBrowserManagedDownloads', 'isAnyDownloading',
    ]),

    totalSizeFormatted() {
      return this.formatBytes(this.totalSizeBytes)
    },

    expiresAtFormatted() {
      if (!this.expiresAt) return ''
      try {
        const date = new Date(this.expiresAt)
        const deltaMs = date.getTime() - Date.now()
        if (deltaMs <= 0) return this.$gettext('expired')
        const totalMinutes = Math.floor(deltaMs / 60000)
        const days = Math.floor(totalMinutes / 1440)
        const hours = Math.floor((totalMinutes % 1440) / 60)
        const minutes = totalMinutes % 60
        if (days > 0) return `${days}d ${hours}h`
        if (hours > 0) return `${hours}h ${minutes}m`
        return `${minutes}m`
      } catch (e) {
        return this.expiresAt
      }
    },
  },

  async mounted() {
    const taskReference = this.$route.params.taskReference
    await this.fetchArchive(taskReference)
  },

  methods: {
    ...mapActions('mediaDownloadLanding', ['fetchArchive', 'startDownload', 'downloadPart']),

    async onDownloadAll() {
      await this.startDownload()
    },

    async onDownloadPart(index) {
      await this.downloadPart(index)
    },

    formatBytes(bytes) {
      if (!bytes || bytes <= 0) return ''
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let value = bytes
      let i = 0
      while (value >= 1024 && i < units.length - 1) {
        value /= 1024
        i++
      }
      const decimals = i >= 2 ? 1 : 0
      return `${value.toFixed(decimals)} ${units[i]}`
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables/_variables";

.download-landing {
  position: relative;
  min-height: 100vh;
  padding: 24px 16px;
  overflow: hidden;
  background: #fff;

  &__bg-circles {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    z-index: 0;

    .circle {
      position: absolute;
      border-radius: 100%;
    }

    .circle-1 {
      top: -200px;
      left: -140px;
      width: 306px;
      height: 306px;
      background: linear-gradient(43deg, #F86B54 0%, #E705C5 100%);
      opacity: 0.85;
    }

    .circle-2 {
      bottom: -300px;
      right: -260px;
      width: 500px;
      height: 500px;
      background: linear-gradient(270deg, #E705C5 0%, #3264F9 100%);
      opacity: 0.85;
    }

    .circle-3 {
      display: none;
    }
  }

  &__wrapper {
    position: relative;
    z-index: 1;
    min-height: 100vh;
  }

  &__card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 16px 48px rgba(31, 41, 84, 0.1);
    padding: 20px 18px;

    @media (min-width: 768px) {
      padding: 24px 24px;
    }
  }

  &__brand {
    text-align: center;
    margin-bottom: 20px;
  }

  &__logo {
    display: inline-block;
  }

  &__hero {
    text-align: center;
    margin-bottom: 18px;
  }

  &__title {
    font-size: 26px;
    font-weight: 700;
    margin: 0 0 10px;
    color: $gray-900;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 15px;
    color: $gray-600;
    margin: 0;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 16px;
  }

  &__btn {
    border-radius: 10px;
  }

  &__section {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid $gray-200;
  }

  &__section-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__section-title {
    font-size: 14px;
    font-weight: $font-weight-semi-bold;
    color: $gray-900;
  }

  &__section-meta {
    font-size: 13px;
    color: $gray-500;
  }

  &__overall {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;

    .overall-bar {
      flex: 1;
      height: 8px;
      background: $gray-200;
      border-radius: 4px;
      overflow: hidden;

      &__fill {
        height: 100%;
        background: linear-gradient(90deg, #3264F9 0%, #E705C5 100%);
        border-radius: 4px;
        transition: width 200ms ease;

        &--done {
          background: $success;
        }
      }

      &__label {
        font-size: 13px;
        font-weight: 600;
        color: $gray-700;
        min-width: 44px;
        text-align: right;
      }
    }
  }

  &__parts {
    list-style: none;
    padding: 0 0 8px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 280px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: none;
    -ms-overflow-style: none;
    mask-image: linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__footer {
    margin-top: 32px;
    background: transparent;
    border: 0;
  }

  &__error {
    text-align: center;
    padding: 24px 0;

    &-icon {
      width: 56px !important;
      height: 56px !important;
      color: $warning;
      margin-bottom: 16px;
    }

    h2 {
      font-size: 22px;
      font-weight: 700;
      margin-bottom: 8px;
    }
  }
}

.stat-card {
  background: $gray-100;
  border-radius: 12px;
  padding: 14px 16px;

  &__label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.06em;
    color: $gray-500;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  &__value {
    font-size: 20px;
    font-weight: 700;
    color: $gray-900;
    line-height: 1.1;
  }
}

.part {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: $gray-100;

  &__icon {
    flex: 0 0 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: linear-gradient(43deg, rgba(248, 107, 84, 0.12) 0%, rgba(231, 5, 197, 0.12) 100%);
    color: #E705C5;

    .c-icon {
      width: 14px;
      height: 14px;
    }

    &--pulsing {
      animation: part-pulse 1.2s ease-in-out infinite;
    }
  }

  &--downloading &__icon {
    background: rgba(50, 100, 249, 0.12);
    color: $primary;
  }

  &--failed &__icon {
    background: rgba(220, 53, 69, 0.1);
    color: $danger;
  }

  &--done &__icon,
  &--started &__icon {
    background: rgba(40, 167, 69, 0.1);
    color: $success;
  }

  &--failed {
    background: rgba(220, 53, 69, 0.08);
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__filename {
    font-weight: $font-weight-semi-bold;
    font-size: 14px;
    color: $gray-900;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  &__sub {
    font-size: 13px;
    color: $gray-500;
    margin-top: 2px;
  }

  &__status {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
  }

  &__status-icon {
    width: 20px;
    height: 20px;

    &--done {
      color: $success;
    }

    &--started {
      color: $success;
    }

    &--retry {
      color: $danger;
      cursor: pointer;
    }

    &--download {
      color: $gray-500;
      cursor: pointer;
      transition: color 120ms ease;

      &:hover {
        color: $primary;
      }
    }

    &--pulsing {
      color: $primary;
      animation: part-pulse 1.2s ease-in-out infinite;
    }
  }

  &__status-percent {
    font-size: 12px;
    font-weight: 600;
    color: $primary;
  }
}

@keyframes part-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.15); opacity: 0.7; }
}

@keyframes part-shimmer {
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
</style>
